import { ref, reactive, computed } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePendenciasStore } from '@/stores/pendencias'

/**
 * Composable de lógica para Workflow de Aprovação
 *
 * @param {Object} config - Configuração da função de aprovação
 * @param {string} config.apiEndpoint - Endpoint base da API (ex: '/OrdensCompra')
 * @param {string} config.entityKey - Nome da chave primária (ex: 'idOrdemCompra')
 * @param {string} config.endpointLista - Sufixo do endpoint de listagem (ex: '/aprovacoes')
 * @param {string} config.endpointAprovar - Sufixo do endpoint de aprovação (ex: '/aprovar')
 * @param {string} config.endpointReprovar - Sufixo do endpoint de reprovação (ex: '/reprovar')
 * @param {string} [config.endpointDetalhes] - Sufixo do endpoint de detalhes (opcional, usa apiEndpoint + id se não informado)
 * @param {string[]} config.listasTabelas - Array de nomes de tabelas para carregar listas do metadata
 * @param {string} config.nomeEntidade - Nome da entidade em minúsculo (ex: 'ordem de compra')
 * @param {string} config.nomeEntidadeTitle - Nome da entidade capitalizado (ex: 'Ordem de Compra')
 * @param {Function} [config.formatarValor] - Função opcional para formatar valores
 * @param {Function} [config.obterSiglaMoeda] - Função opcional para obter sigla da moeda
 * @param {Function} [config.formatarMoedaComSigla] - Função opcional para formatar moeda com sigla
 * @param {Function} [config.getTextoLista] - Função opcional para resolver texto de lista
 * @param {Object} [config.filtrosPadrao] - Valores padrão dos filtros
 * @param {Function} [config.emitNotification] - Callback para notificações (type, message)
 */
export function useWorkflowAprovacao (config) {
  const authStore = useAuthStore()
  const metadataStore = useMetadataListasStore()
  const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

  // --- Estado ---
  const lista = ref([])
  const loading = ref(false)
  const erro = ref(null)

  const sortField = ref('')
  const sortOrder = ref('')

  const filtros = reactive(config.filtrosPadrao || {})

  const listas = reactive({})

  // --- Modal de análise ---
  const modalAberto = ref(false)
  const itemDetalhes = ref(null)
  const isLoadingDetalhes = ref(false)
  const visaoCompleta = ref(false)
  const reprovarModalRef = ref(null)
  const aprovarModalRef = ref(null)
  const reprovarObservacao = ref('')
  const aprovarObservacao = ref('')
  const idItemAnalise = ref(null)
  const indiceAtual = ref(-1)
  const estaProcessandoAcao = ref(false)
  const confirmacaoAtiva = ref(true)

  // --- Decisões customizadas (JSON OpcoesAprovacaoJson da solicitação) ---
  // Quando o solicitante define opções, o modal de análise exibe os botões
  // correspondentes (prioritários); caso contrário, o fallback Aprovar/Reprovar.
  const decisoesCustomizadas = computed(() => {
    const raw = itemDetalhes.value?.opcoesAprovacaoJson
    if (!raw) return []
    try {
      const arr = JSON.parse(raw)
      if (!Array.isArray(arr)) return []
      // O DTO OpcaoAprovacaoDto usa PascalCase (Decisao, LabelBotao, Status)
      return arr.filter(o => o && (o.decisao || o.Decisao) && (o.labelBotao || o.LabelBotao))
    } catch (e) {
      console.warn('Erro ao interpretar OpcoesAprovacaoJson:', e)
      return []
    }
  })

  const temDecisoesCustomizadas = computed(() => decisoesCustomizadas.value.length > 0)

  const totalPendentes = computed(() => lista.value.length)
  const itemAtual = computed(() => lista.value[indiceAtual.value] || null)
  const posicaoLabel = computed(() => {
    if (indiceAtual.value < 0 || totalPendentes.value === 0) return ''
    return `${indiceAtual.value + 1} de ${totalPendentes.value}`
  })
  const temProxima = computed(() => indiceAtual.value < totalPendentes.value - 1)
  const temAnterior = computed(() => indiceAtual.value > 0)

  // --- Helpers de formatação (fallback para funções da config) ---
  const formatarValor = config.formatarValor || ((valor) => {
    if (valor == null) return '-'
    try {
      return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
    } catch (e) {
      return valor?.toFixed(2) ?? '-'
    }
  })

  const getTextoLista = config.getTextoLista || ((lista, id) => {
    if (!id || !lista) return id || '-'
    const idStr = String(id)
    // Listas do Cliente usam idListaClienteItem; listas do Sistema usam idListaSistemaItem
    const item = lista.find(i => String(i.idListaClienteItem ?? i.idListaSistemaItem) === idStr)
    return item ? item.textoLista : idStr
  })

  const obterSiglaMoeda = config.obterSiglaMoeda || ((codigoMoeda) => {
    if (!codigoMoeda) return 'BRL'
    const idStr = String(codigoMoeda)
    const itemLista = (listas.LstMoedaPadrao || []).find(m => String(m.idListaClienteItem) === idStr)
    const texto = itemLista ? itemLista.textoLista : codigoMoeda
    const match = String(texto).match(/^[A-Z]{3}/)
    return match ? match[0] : codigoMoeda
  })

  const formatarMoedaComSigla = config.formatarMoedaComSigla || ((valor, codigoMoeda) => {
    const sigla = obterSiglaMoeda(codigoMoeda)
    return `${formatarValor(valor)} ${sigla}`
  })

  // --- Carregamento de listas ---
  const carregarListas = async () => {
    try {
      const result = await metadataStore.carregarListasParaTabelas(config.listasTabelas)
      Object.assign(listas, result)
    } catch (e) {
      console.warn('Erro ao carregar listas:', e)
    }
  }

  // --- Busca de dados ---
  const buscarDados = async (pageNumber = 1) => {
    if (authStore.isAdminInterno && !authStore.empresaAtiva) {
      erro.value = 'Por favor, selecione uma Empresa no topo da página.'
      lista.value = []
      return
    }

    loading.value = true
    erro.value = null

    try {
      const params = injetarParametros(pageNumber)

      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)

      // Filtros dinâmicos
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value)
        }
      })

      const separator = config.endpointLista.includes('?') ? '&' : '?'
      const response = await api.get(`${config.apiEndpoint}${config.endpointLista}${separator}${params.toString()}`)
      lista.value = response.data

      atualizarPaginacao(response.headers)
    } catch (error) {
      erro.value = 'Não foi possível carregar as aprovações pendentes.'
      lista.value = []
      paginacao.value.TotalCount = 0
    } finally {
      loading.value = false
    }
  }

  // --- Filtros ---
  const aplicarFiltros = () => {
    buscarDados(1)
  }

  const limparFiltros = () => {
    Object.keys(filtros).forEach(key => {
      filtros[key] = null
    })
    buscarDados(1)
  }

  // --- Paginação ---
  const mudarPagina = (page) => {
    if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
      buscarDados(page)
    }
  }

  const mudarTamanhoPagina = (novoTamanho) => {
    paginacao.value.PageSize = novoTamanho
    buscarDados(1)
  }

  // --- Navegação entre itens no modal ---
  const carregarPorIndice = async (novoIndice) => {
    if (novoIndice < 0 || novoIndice >= totalPendentes.value) return

    const item = lista.value[novoIndice]
    if (!item) return

    indiceAtual.value = novoIndice
    idItemAnalise.value = item[config.entityKey]
    visaoCompleta.value = false
    isLoadingDetalhes.value = true
    itemDetalhes.value = null

    try {
      const endpointDetalhes = config.endpointDetalhes
        ? `${config.apiEndpoint}${config.endpointDetalhes}/${item[config.entityKey]}`
        : `${config.apiEndpoint}/${item[config.entityKey]}`
      const response = await api.get(endpointDetalhes)
      itemDetalhes.value = response.data
    } catch (error) {
      emitNotification('error', `Erro ao carregar detalhes ${config.nomeEntidade}.`)
      modalAberto.value = false
    } finally {
      isLoadingDetalhes.value = false
    }
  }

  const emitNotification = (type, message) => {
    if (config.emitNotification) {
      config.emitNotification(type, message)
    }
  }

  const abrirModalAnalise = async (item) => {
    const idx = lista.value.findIndex(i => i[config.entityKey] === item[config.entityKey])
    if (idx >= 0) {
      await carregarPorIndice(idx)
    } else {
      // Item não está na lista (ex: veio de notificação antes do carregamento)
      indiceAtual.value = -1
      idItemAnalise.value = item[config.entityKey]
      visaoCompleta.value = false
      isLoadingDetalhes.value = true
      itemDetalhes.value = null
      try {
        const endpointDetalhes = config.endpointDetalhes
          ? `${config.apiEndpoint}${config.endpointDetalhes}/${item[config.entityKey]}`
          : `${config.apiEndpoint}/${item[config.entityKey]}`
        const response = await api.get(endpointDetalhes)
        itemDetalhes.value = response.data
      } catch (error) {
        emitNotification('error', `Erro ao carregar detalhes ${config.nomeEntidade}.`)
        modalAberto.value = false
      } finally {
        isLoadingDetalhes.value = false
      }
    }
    modalAberto.value = true
  }

  const proximo = () => {
    if (temProxima.value) {
      carregarPorIndice(indiceAtual.value + 1)
    }
  }

  const anterior = () => {
    if (temAnterior.value) {
      carregarPorIndice(indiceAtual.value - 1)
    }
  }

  const fecharModal = () => {
    modalAberto.value = false
    itemDetalhes.value = null
    reprovarObservacao.value = ''
    aprovarObservacao.value = ''
    indiceAtual.value = -1
  }

  const alternarVisao = () => {
    visaoCompleta.value = !visaoCompleta.value
  }

  // --- Ações de aprovação com auto-avanço ---
  const confirmarReprovacao = () => {
    reprovarObservacao.value = ''
    reprovarModalRef.value?.show()
  }

  const confirmarAprovacao = () => {
    aprovarObservacao.value = ''
    if (confirmacaoAtiva.value) {
      aprovarModalRef.value?.show()
    } else {
      executarAprovacao(true)
    }
  }

  const avancarOuFecharAposAcao = () => {
    // Remove o item atual da lista (foi aprovado/reprovado)
    lista.value.splice(indiceAtual.value, 1)

    if (totalPendentes.value === 0) {
      // Não há mais itens pendentes — fecha o modal
      fecharModal()
      buscarDados(1)
      return
    }

    // Se o índice atual ultrapassou o tamanho, vai para o último
    if (indiceAtual.value >= totalPendentes.value) {
      indiceAtual.value = totalPendentes.value - 1
    }

    // Carrega o próximo item (ou o anterior se estiver no último)
    carregarPorIndice(indiceAtual.value)
  }

  const executarAprovacao = async (confirmou) => {
    if (!confirmou || !idItemAnalise.value || estaProcessandoAcao.value) return
    estaProcessandoAcao.value = true

    const textarea = document.getElementById('observacaoAprovacao')
    aprovarObservacao.value = textarea ? textarea.value.trim() : ''
    try {
      await api.post(`${config.apiEndpoint}/${idItemAnalise.value}${config.endpointAprovar}`, {
        acao: 'APROVAR',
        observacao: aprovarObservacao.value || null,
        xmin: itemDetalhes.value?.xmin ?? itemAtual.value?.xmin
      })
      emitNotification('success', `${config.nomeEntidadeTitle} aprovada com sucesso.`)
      usePendenciasStore().atualizarContagem()
      avancarOuFecharAposAcao()
    } catch (error) {
      if (error.response?.status === 403) {
        emitNotification('error', `Você não tem permissão para aprovar ${config.nomeEntidade}.`)
      } else {
        const msg = error.response?.data?.message || `Erro ao aprovar ${config.nomeEntidade}.`
        emitNotification('error', msg)
      }
    } finally {
      estaProcessandoAcao.value = false
    }
  }

  const executarReprovacao = async (confirmou) => {
    if (!confirmou || !idItemAnalise.value || estaProcessandoAcao.value) return
    estaProcessandoAcao.value = true

    const textarea = document.getElementById('observacaoReprovacao')
    reprovarObservacao.value = textarea ? textarea.value.trim() : ''
    try {
      await api.post(`${config.apiEndpoint}/${idItemAnalise.value}${config.endpointReprovar}`, {
        acao: 'REPROVAR',
        observacao: reprovarObservacao.value || null,
        xmin: itemDetalhes.value?.xmin ?? itemAtual.value?.xmin
      })
      emitNotification('success', `${config.nomeEntidadeTitle} reprovada.`)
      usePendenciasStore().atualizarContagem()
      avancarOuFecharAposAcao()
    } catch (error) {
      if (error.response?.status === 403) {
        emitNotification('error', `Você não tem permissão para reprovar ${config.nomeEntidade}.`)
      } else {
        const msg = error.response?.data?.message || `Erro ao reprovar ${config.nomeEntidade}.`
        emitNotification('error', msg)
      }
    } finally {
      estaProcessandoAcao.value = false
    }
  }

  // --- Decisão customizada (JSON OpcoesAprovacaoJson) ---
  // Executa uma análise com a decisão escolhida pelo aprovador, chamando o endpoint
  // unificado "{id}/analisar". O backend valida a decisão contra as opções da
  // solicitação (ou aceita APROVAR/REPROVAR quando não há opções customizadas).
  const executarDecisaoCustomizada = async (opcao) => {
    if (!idItemAnalise.value || estaProcessandoAcao.value) return
    estaProcessandoAcao.value = true

    const textareaOpcao = document.getElementById(`observacaoDecisao_${idItemAnalise.value}`)
    const observacao = textareaOpcao ? textareaOpcao.value.trim() : ''
    const xmin = itemDetalhes.value?.xmin ?? itemAtual.value?.xmin

    try {
      await api.post(`${config.apiEndpoint}/${idItemAnalise.value}/analisar`, {
        acao: opcao.status || opcao.Status || 'APROVAR',
        decisao: opcao.decisao || opcao.Decisao,
        observacao: observacao || null,
        xmin
      })
      emitNotification('success', `Análise concluída: ${opcao.labelBotao || opcao.LabelBotao}.`)
      usePendenciasStore().atualizarContagem()
      avancarOuFecharAposAcao()
    } catch (error) {
      if (error.response?.status === 403) {
        emitNotification('error', `Você não tem permissão para analisar ${config.nomeEntidade}.`)
      } else {
        const msg = error.response?.data?.message || `Erro ao executar a decisão ${opcao.labelBotao}.`
        emitNotification('error', msg)
      }
    } finally {
      estaProcessandoAcao.value = false
    }
  }

  const handleSortChange = () => {
    buscarDados(1)
  }

  return {
    // Estado
    lista,
    loading,
    erro,
    sortField,
    sortOrder,
    filtros,
    listas,
    paginacao,

    // Modal
    modalAberto,
    itemDetalhes,
    isLoadingDetalhes,
    visaoCompleta,
    reprovarModalRef,
    aprovarModalRef,
    reprovarObservacao,
    aprovarObservacao,
    idItemAnalise,
    indiceAtual,
    estaProcessandoAcao,
    confirmacaoAtiva,

    // Computados
    totalPendentes,
    itemAtual,
    posicaoLabel,
    temProxima,
    temAnterior,

    // Decisões customizadas (JSON OpcoesAprovacaoJson)
    decisoesCustomizadas,
    temDecisoesCustomizadas,
    executarDecisaoCustomizada,

    // Helpers
    formatarValor,
    getTextoLista,
    obterSiglaMoeda,
    formatarMoedaComSigla,

    // Métodos
    carregarListas,
    buscarDados,
    aplicarFiltros,
    limparFiltros,
    mudarPagina,
    mudarTamanhoPagina,
    carregarPorIndice,
    abrirModalAnalise,
    proximo,
    anterior,
    fecharModal,
    alternarVisao,
    confirmarReprovacao,
    confirmarAprovacao,
    executarAprovacao,
    executarReprovacao,
    avancarOuFecharAposAcao,
    handleSortChange
  }
}
